import { useAppState } from '@/lib/hooks/use-app-state';
import type { CrmEventDetails } from '@/server/types/infer-trpc-types';
import { api } from '@/utils/api';
import { getFullName } from '@/utils/name';
import { SrCustomerStatus } from '@plato/data/starrocks/types';

interface UseUpdateEventOptions {
    onSuccess?: (data?: unknown) => void;
    onError?: (message?: string) => void;
}

export function useUpdateEvent(options: UseUpdateEventOptions = {}) {
    const apiUtils = api.useUtils();
    const { platoUser } = useAppState();

    const updateEventMutation = api.platoUser.command.events.update.useMutation({
        async onMutate({ eventId, data }) {
            const previousData = apiUtils.platoUser.query.events.getEventById.getData({ eventId });
            await apiUtils.platoUser.query.events.getEventById.cancel({ eventId });
            apiUtils.platoUser.query.events.getEventById.setData(
                { eventId },
                {
                    order: {},
                    notes: [],
                    ...previousData,
                    event_id: eventId,
                    customerId: data.customer_id,
                    customer:
                        previousData?.customer?.customerId === data.customer_id
                            ? previousData.customer
                            : ({
                                  customerId: data.customer_id,
                                  customerName: data.customer_id,
                                  salesReps: [],
                                  address: {
                                      addressId: '',
                                      customerId: '',
                                  },
                                  platoStatus: SrCustomerStatus.INACTIVE,
                              } satisfies CrmEventDetails['customer']),
                    date: data.date,
                    startDate: data.date,
                    start_time: data.start_time,
                    end_time: data.end_time,
                    address: data.address,
                    description: data.description as string,
                    owner_id: previousData?.owner_id ?? platoUser?.id ?? '',
                    owner_name:
                        previousData?.owner_name ?? getFullName(platoUser?.firstName, platoUser?.lastName),
                },
            );
            return { previousData };
        },
        onError(error, { eventId }, context) {
            if (context?.previousData) {
                apiUtils.platoUser.query.events.getEventById.setData({ eventId }, context.previousData);
            }
            apiUtils.platoUser.query.events.getEventById.invalidate({ eventId });
            options.onError?.(error instanceof Error ? error.message : String(error));
        },
        onSuccess(_, { eventId, data: { customer_id } }, context) {
            if (eventId) {
                apiUtils.platoUser.query.events.getEventById.invalidate({ eventId });
            }
            apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
            apiUtils.platoUser.query.events.getList.invalidate();
            apiUtils.platoUser.query.events.getUpcomingCustomerEvents.invalidate({
                customerId: customer_id,
            });
            const customerIdBefore = context?.previousData?.customer?.customerId;
            if (customerIdBefore !== customer_id) {
                apiUtils.platoUser.query.events.getUpcomingCustomerEvents.invalidate({
                    customerId: customerIdBefore,
                });
            }
            options.onSuccess?.();
        },
    });

    return updateEventMutation;
}
